import { useState, useEffect, FC } from "react";
import { callErrorToast } from "../../../utilities/index.js";
import { ErrorResponse } from "../../../Models/Error.ts";
import { WarehouseArticleSettingsService } from "../../../services/logistic/warehouseArticleSettings.ts";
import { WarehouseArticleSettings } from "../../../Models/WarehouseArticleSettings.tsx";
import {
  WarehouseArticleSettingsDeleteContextValue,
  WarehouseArticleSettingsDeleteProviderProps,
} from "./types.ts";
import { WarehouseArticleSettingsDeleteContext } from "./contexts.ts";

/**
 * @component WarehouseArticleSettingsDeleteProvider
 * Provider component responsible for managing the state and logic for batch deletion
 * of WarehouseArticleSettings entries. Handles API calls, loading state, error reporting,
 * and success callback execution.
 *
 * @param {WarehouseArticleSettingsDeleteProviderProps} props - The component properties.
 * @returns React elements with the WarehouseArticleSettingsDeleteContext provided.
 */
const WarehouseArticleSettingsDeleteProvider: FC<
  WarehouseArticleSettingsDeleteProviderProps
> = ({ children, data = [], callback }) => {
  // Access the service instance
  const warehouseArticleSettingsService = new WarehouseArticleSettingsService();

  /**
   * @var error
   * State to hold any error response received during the API call.
   */
  const [error, setError] =
    useState<ErrorResponse<WarehouseArticleSettings> | null>(null);

  /**
   * @var loader
   * Boolean state indicating if the deletion process is currently loading.
   */
  const [loader, setLoader] = useState<boolean>(false);

  /**
   * @function deleteWarehouseArticleSettings
   * Executes deletion of all IDs in the 'data' array concurrently using Promise.allSettled.
   * Handles success (calls callback) and failure (sets error state).
   */
  const deleteWarehouseArticleSettings = async (): Promise<void> => {
    setLoader(true);

    try {
      const results = await Promise.allSettled(
        data.map((id) => warehouseArticleSettingsService.delete(id)),
      );

      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const success = results.filter(
        (r): r is PromiseFulfilledResult<void> => r.status === "fulfilled",
      );

      if (failed.length > 0) {
        const firstError = failed[0]
          .reason as ErrorResponse<WarehouseArticleSettings>;
        setError(firstError);

        if (success.length > 0 && callback) {
          callback();
        }

        return;
      }

      if (callback) callback();
    } catch (unexpected) {
      const err: ErrorResponse<WarehouseArticleSettings> = {
        response: { data: {} },
      };
      setError(err);
    } finally {
      setLoader(false);
    }
  };

  /**
   * @hook useEffect
   * Watches for changes in the 'error' state. If an error exists, triggers a toast notification.
   * The error state can be reset after showing the toast if desired.
   */
  useEffect(() => {
    if (error) {
      callErrorToast(error);
      // Optional: setError(null) to allow toast re-triggering without leaving the component
    }
  }, [error]);

  /**
   * @var contextValue
   * The assembled value passed down to all consumers of the Context.
   */
  const contextValue: WarehouseArticleSettingsDeleteContextValue = {
    loader,
    deleteWarehouseArticleSettings,
    data,
    callback,
  };

  return (
    <WarehouseArticleSettingsDeleteContext.Provider value={contextValue}>
      {children}
    </WarehouseArticleSettingsDeleteContext.Provider>
  );
};

export { WarehouseArticleSettingsDeleteProvider };
